import { useEffect, useMemo, useRef } from 'react';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import { useFpId } from '../../../../../hooks/useFpId';
import { SOCIAL_SECURITY_STRATEGIES, SocialSecurityWhatIf } from './api/types';
import { SocialSecurityStrategy } from './api/useAnnualBenefitsSummaryQuery';
import {
  SocialSecurityComparisonModel,
  useSocialSecurityComparisonQuery,
} from './api/useSocialSecurityComparisonQuery';
import { useSocialSecurityComparisonWithWhatIfsMutation } from './api/useSocialSecurityComparisonWithWhatIfsMutation';
import { HEADER_MAP } from './SocialSecurity.meta';
import { useSocialSecurityWhatIfRefreshToken } from './api/useSocialSecurityWhatIfRefresh';

interface Params {
  isWhatIfsActive?: boolean;
  whatIfsBody?: SocialSecurityWhatIf[];
  planName?: string;
}

export const useSocialSecurity = ({
  isWhatIfsActive = false,
  whatIfsBody = [],
  planName = 'Current Plan',
}: Params = {}) => {
  const clientId = useClientId();
  const fpId = useFpId();
  const whatIfRefreshToken = useSocialSecurityWhatIfRefreshToken(clientId, fpId);

  const {
    isFetching: isQueryFetching,
    data: queryData,
    error: queryError,
    refetch,
  } = useSocialSecurityComparisonQuery({
    clientId,
    fpId,
    enabled: !isWhatIfsActive,
  });

  const mutation = useSocialSecurityComparisonWithWhatIfsMutation({ clientId, fpId });
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isWhatIfsActive) {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      mutation.mutate({ clientId, fpId, body: whatIfsBody, signal: controller.signal });
    }
  }, [isWhatIfsActive, whatIfsBody, clientId, fpId, whatIfRefreshToken]);

  // The what-ifs mutation fires in a post-mount effect, so on the first render it is
  // still idle (not loading, no data). Treat that idle window as loading to avoid
  // briefly rendering an empty table before the mutate call runs.
  const isFetching = isWhatIfsActive ? mutation.isLoading || mutation.isIdle : isQueryFetching;
  const strategies = isWhatIfsActive ? mutation.data : queryData;
  const error = isWhatIfsActive ? mutation.error : queryError;

  const data = useMemo(() => {
    // The response also carries metadata (PrincipalName, SpouseName, HasSocialSecurityIncome,
    // PrincipalIsCurrentlyReceivingBenefits, SpouseIsCurrentlyReceivingBenefits) which
    // passes a plain != null check and would leak in as bogus columns. Restrict to the
    // known strategy keys so the grid stays aligned with the Monte Carlo probabilities.
    const validStrategies = strategies
      ? (Object.entries(strategies).filter(
          ([key, strategy]) =>
            SOCIAL_SECURITY_STRATEGIES.includes(key as SocialSecurityStrategy) && strategy != null,
        ) as [SocialSecurityStrategy, SocialSecurityComparisonModel][])
      : [];

    if (validStrategies.length === 0) {
      return null;
    }

    const rowNames = validStrategies[0][1];

    const tableData = Object.keys(rowNames).reduce((acc, rowKey) => {
      return {
        ...acc,
        [rowKey]: validStrategies.reduce<
          SocialSecurityComparisonModel[keyof SocialSecurityComparisonModel][]
        >((accumulator, [, strategy]) => {
          return [...accumulator, strategy[rowKey as keyof SocialSecurityComparisonModel]];
        }, []),
      };
    }, {} as { [K in keyof SocialSecurityComparisonModel]: SocialSecurityComparisonModel[K][] });

    const headerNames = validStrategies.map(([key]) => ({
      label: HEADER_MAP(planName)[key as keyof typeof HEADER_MAP],
      value: key,
    })) as { label: string; value: SocialSecurityStrategy }[];

    const spouseName = strategies?.SpouseName || null;

    const benefitStartAge = tableData.BenefitStartAgePrincipal.map((value, index) => {
      const principal = `${value ?? '-'}`;
      if (!spouseName) return principal;
      return `${principal} / ${tableData.BenefitStartAgeSpouse[index] ?? '-'}`;
    });

    const firstYearBenefit = tableData.FirstYearBenefitPrincipal.map((value, index) => {
      const principal = value !== null ? formatCurrency(value, { decimalPlaces: 0 }) : '-';
      if (!spouseName) return principal;
      const spouseValue = tableData.FirstYearBenefitSpouse[index];
      return `${principal} / ${
        spouseValue !== null ? formatCurrency(spouseValue, { decimalPlaces: 0 }) : '-'
      }`;
    });

    const breakEvenAge = tableData.BreakevenAgePrincipal.map((value, index) => {
      const principal = `${value ?? '-'}`;
      if (!spouseName) return principal;
      return `${principal} / ${tableData.BreakevenAgeSpouse[index] ?? '-'}`;
    });

    const totalLifetimeBenefit = tableData.TotalLifetimeBenefitPrincipal.map((value, index) => {
      const principal = value !== null ? formatCurrency(value, { decimalPlaces: 0 }) : '-';
      if (!spouseName) return principal;
      const spouseValue = tableData.TotalLifetimeBenefitSpouse[index];
      return `${principal} / ${
        spouseValue !== null ? formatCurrency(spouseValue, { decimalPlaces: 0 }) : '-'
      }`;
    });

    const retirementAssetsAtEndOfPlan = tableData.RetirementAssetsAtEndOfPlan.map(value =>
      value !== null ? formatCurrency(value, { decimalPlaces: 0 }) : '-',
    );
    const netWorthAtEndOfPlan = tableData.NetWorthAtEndOfPlan.map(value =>
      value !== null ? formatCurrency(value, { decimalPlaces: 0 }) : '-',
    );

    const appliedStrategies = validStrategies
      .filter(([key, strategy]) => key !== 'ActivePlan' && strategy.IsApplied)
      .map(([key]) => key);

    const applicableStrategies = validStrategies
      .filter(([, strategy]) => strategy.CanApply)
      .map(([key]) => key);

    return {
      headerNames,
      appliedStrategies,
      applicableStrategies,
      benefitStartAge,
      firstYearBenefit,
      breakEvenAge,
      totalLifetimeBenefit,
      retirementAssetsAtEndOfPlan,
      netWorthAtEndOfPlan,
      // First names, served by the endpoint so the spouse's doesn't have to be parsed out of
      // CurrentClient.PartnerName (a full name).
      principalName: strategies?.PrincipalName || 'Principal',
      spouseName,
      principalIsCurrentlyReceivingBenefits:
        strategies?.PrincipalIsCurrentlyReceivingBenefits ?? false,
      spouseIsCurrentlyReceivingBenefits: strategies?.SpouseIsCurrentlyReceivingBenefits ?? false,
    };
  }, [strategies, planName]);

  return {
    isFetching,
    data,
    error,
    refetch,
  };
};
